import { PosableMapObject, Twist, Vec3 } from "../../../../models/SyntheticMap";
import * as THREE from "three";
import { constrain, isClose as isValueClose, normalizeAngleToPi } from "../../../../utils/spatial";
import { DragonflyOrbitController } from "./DragonflyOrbitController";
import { DragonflyLandController } from "./DragonflyLandController";
import { DragonflyNavigateController } from "./DragonflyNavigateController";
import { DragonflyController } from "./DragonflyController";
import { DragonflyAgent } from "../DragonflyAgent";
import { CameraConfig } from "../../../../visuals/ModelBasedVisual";
import { copyVec3 } from "../../../../visuals/visual-utils";
import { CameraController } from "./CameraController";

interface TwistProgram {
  twist: PTwist;
  duration: number;
}

interface StateScheduler {
  state: string;
  condition?: () => boolean;
}

const ZeroTwist: Twist = {
  linear: new THREE.Vector3(0, 0, 0),
  angular: new THREE.Vector3(0, 0, 0),
}

function sv(v: Partial<Vec3>): Vec3 {
  return {
    x: v.x ?? 0,
    y: v.y ?? 0,
    z: v.z ?? 0
  };
}
interface PTwist {
  linear?: Partial<Vec3>;
  angular?: Partial<Vec3>;
}
function st(v: PTwist): Twist {
  if (!v) {
    return ZeroTwist;
  }
  if (!v.linear) {
    v.linear = { x: 0, y: 0, z: 0 };
  }
  if (!v.angular) {
    v.angular = { x: 0, y: 0, z: 0 };
  }

  return {
    linear: sv(v.linear),
    angular: sv(v.angular)
  };
}

export interface DragonflyMapObject extends PosableMapObject {
  target: Vec3; // target position for the dragonfly
  orbitRadius: number; // meters
  orbitAltitude: number; // meters
  flightPlan: string[]; // destination for the dragonfly, used for flight planning
}

export class DragonflyStateManager {
  private stateScheduler: StateScheduler[] = [];
  private twistSchedule: TwistProgram[] = [];
  public target: Vec3 = { x: 0, y: 0, z: 0 }; // target position for the dragonfly
  public landingTarget: Vec3 = { x: 0, y: 0, z: 0 }; // target position for landing
  public orbitRadius = 50.0; // meters
  public orbitAltitude = 30.0; // meters
  public flightPlan: string[] = []; // destination for the dragonfly, used for flight planning
  private orbitSpeed = 5.0; // m/sec
  public propSpeed = 0.0; // rad/secnpm
  private controller: DragonflyController | null = null;
  private inTheAir = false;
  private props: THREE.Object3D[] = [];
  private craft: THREE.Object3D;
  private flightpathVisualizer: THREE.Object3D | null = null;
  public cameraGimbal: THREE.Group;
  public cameraFov = 30; // field of view in degrees
  public cameraTracksTarget = false; // whether the camera should track the target position
  public obj: THREE.Object3D;
  public twist: Twist = ZeroTwist;
  public isSelected = false;
  cameraController: CameraController;

  constructor(public agent: DragonflyAgent) {
    this.craft = agent.visual.obj.children[0];
    this.obj = agent.visual.obj;
    // this.craft.getObjectByName("AxissHelper")?.removeFromParent();
    // const ah = new THREE.AxesHelper(1.0);
    // this.craft.add(ah);
    copyVec3(this.agent.visual.position, this.landingTarget);
    copyVec3(this.agent.visual.position, this.target);
    this.craft.getObjectByName("CameraGimbal")?.removeFromParent();
    this.cameraGimbal = new THREE.Group();
    this.cameraGimbal.name = "CameraGimbal";

    const centerMesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.05, 16, 16),
      new THREE.MeshBasicMaterial({ color: 0xff0000 })
    );
    this.cameraGimbal.add(centerMesh);
    centerMesh.visible = false;
    const cameraMesh = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.2, 0.5, 32),
      new THREE.MeshBasicMaterial({ color: 0x00ff00, transparent: true, opacity: 0.25 })
    );
    cameraMesh.visible = false;
    cameraMesh.rotation.z = Math.PI / 2;
    cameraMesh.position.set(0.25, 0, 0);
    this.cameraGimbal.add(cameraMesh);
    this.cameraGimbal.rotation.order = "ZYX"; // Set rotation order to YXZ
    // this.cameraGimbal.rotation.x = -Math.PI / 3; // Rotate to horizontal
    this.cameraGimbal.position.set(-0.8, -0.0, -0.150);
    this.craft.add(this.cameraGimbal);

    this.cameraController = new CameraController(agent, this.cameraGimbal);
    this.cameraController.objectReference = agent.visual.obj;
    this.cameraController.frameReference = agent.visual.wr.dataService.activeSite!.obj;
    this.cameraController.craft = this.craft;

    this.props = [
      this.craft.getObjectByName("PropForward") as THREE.Object3D,
      this.craft.getObjectByName("PropAft") as THREE.Object3D,
    ];
    this.agent.addState("Stage", async () => {
      this.agent.disableAllStates();
      this.craft.rotation.y = DragonflyController.PITCH_NORM;
      return true;
    })
    this.agent.addState("Idle", async () => {
      await this.agent.disableAllStates();
      this.inTheAir = false;
      this.craft.rotation.y = DragonflyController.PITCH_VERT;
      this.craft.rotation.z = 0;
      this.craft.rotation.x = 0;
      this.schedulerStatus = 'Idle';
      copyVec3(this.landingTarget, this.obj.position);
      copyVec3(this.agent.visual.rotation, this.obj.rotation);
      // this.obj.position.z = this.target.z;
      this.twist = ZeroTwist;
      this.propSpeed = 0.0; // rad/sec
      this.startController(null);
      this.scheduleTwist([]);
      return true;
    }, async () => {
      this.propSpeed = 0.0; // rad/sec
      this.scheduleTwist([]);
      this.startController(null);
    });
    this.agent.addState("TakeOff", async () => {
      await this.agent.disableAllStates('Selected', 'Sentry Mode');
      this.schedulerStatus = 'Take Off';
      this.propSpeed = 30.0; // rad/sec
      this.scheduleTwist([
        { twist: { linear: { z: 0.2 } }, duration: 1 },
        { twist: { linear: { z: 1.0 } }, duration: 2 },
        { twist: { linear: { z: 2.5 } }, duration: 10 },
        { twist: { linear: { z: 1.0 } }, duration: 1 },
        { twist: { linear: { z: 0.0 } }, duration: 1 },
        { twist: { angular: { y: -0.6 }, linear: { x: 1.0, z: -0.2 } }, duration: 1 },
        { twist: { angular: { y: -0.85 }, linear: { x: 5.0, z: -0.5 } }, duration: 1 },
        { twist: { angular: {}, linear: { x: 7.5 } }, duration: 1 },
        // { twist: { linear: { x: 2 } }, duration: 1 },
      ]);
      this.inTheAir = true;
      return true;
    }, async () => {
      this.propSpeed = 0.0; // rad/sec
      this.scheduleTwist([]);
    });
    this.agent.addState("Orbit", async () => {
      await this.agent.disableAllStates('Selected', 'Sentry Mode');
      if (!this.inTheAir) {
        this.stateScheduler = [
          { state: "Orbit" },
        ];
        this.agent.enableState("TakeOff");
        return false;
      }
      this.craft.rotation.y = DragonflyController.PITCH_NORM;
      // this.cameraTracksTarget = true; // enable camera tracking
      // this.disableActorState("TakeOff");
      this.propSpeed = 10.0; // rad/sec
      this.startController(new DragonflyOrbitController(
        this.obj,
        this.target,
        this.orbitRadius,
        this.orbitAltitude,
        this.orbitSpeed * this.agent.maxSpeed));
      return true;
    }, async () => {
      this.cameraTracksTarget = false; // disable camera tracking
      this.propSpeed = 0.0; // rad/sec
      this.scheduleTwist([]);
      this.startController(null);
      if (this.flightpathVisualizer) {
        this.flightpathVisualizer.parent?.remove(this.flightpathVisualizer);
        this.flightpathVisualizer = null;
      }
    });

    this.agent.addState("Land", async () => {
      if (!this.inTheAir) {
        return false;
      }
      await this.agent.disableAllStates();
      this.schedulerStatus = 'Land';
      const alt = this.obj.position.z - this.landingTarget.z;
      this.startController(new DragonflyLandController(this.obj, this.craft,
        this.agent.maxSpeed * 2.0,
        this.landingTarget, this.agent.visual.rotation));
      return true;
    }, async () => {
      this.propSpeed = 0.0; // rad/sec
      this.scheduleTwist([]);
      this.startController(null);
    });

    this.agent.addState("Navigate", async () => {
      await this.agent.disableAllStates();
      if (!this.inTheAir) {
        this.stateScheduler = [
          { state: "Navigate" },
        ];
        this.agent.enableState("TakeOff");
        return false;
      }
      this.craft.rotation.y = Math.PI / 2;
      this.startController(new DragonflyNavigateController(
        this.obj,
        this.flightPlan,
        this.orbitAltitude * 1.5,
        this.agent.maxSpeed * 3.0));
      return true;
    }, async () => {
    });

    this.agent.addState("Selected", async () => {
      console.log('SELECTED TRIGGERED');
      this.isSelected = true;
      // this.agent.setSentry(true);
      this.controller?.visualization(true);
      return true;
    }, async () => {
      // this.agent.setSentry(false);
      this.isSelected = false;
      this.controller?.visualization(false);
    });

    this.agent.enableState("Idle");
    // this.agent.enableState("Stage");
    // this.agent.enableState("Selected");
  }

  public acceptsScenarioUpdates(): boolean {
    return false;
  }

  public startController(controller: DragonflyController | null): void {
    if (this.controller && this.isSelected) {
      this.controller.visualization(false);
    }
    this.controller = controller;
    if (this.controller) {
      this.controller.frameReference = this.agent.visual.wr.dataService.activeSite!.obj;
      this.controller.objectReference = this.agent.visual.obj;
    }
    if (this.controller && this.isSelected) {
      this.controller.visualization(true);
    }
  }

  public get altitudeInd(): string {
    return this.obj.position.z.toFixed(2) + " m";
  }

  public get speedInd(): string {
    return this.twist.linear.x.toFixed(2) + " m/s";
  }

  public get headingInd(): string {
    return (this.obj.rotation.z * 180 / Math.PI).toFixed(2) + "°";
  }
  private schedulerStatus = 'Transitioning';
  public get statusInd(): string {
    if (this.controller) {
      return this.controller.getStatus();
    }
    return this.schedulerStatus;
  }

  private scheduleTwist(p: TwistProgram[]): void {
    this.twistSchedule = p;
  }

  public render(delta: number): void {
    if (this.controller) {
      this.propSpeed = this.controller.getPropSpeed();
    }
    if (this.propSpeed > 0) {
      let sign = 1;
      for (const prop of this.props) {
        prop.rotation.z += sign * this.propSpeed * delta;
        prop.rotation.z = normalizeAngleToPi(prop.rotation.z);
        sign *= -1; // alternate prop direction
      }
    }
    if (!this.controller) {
      const step = this.twistSchedule[0];
      if (!step) {
      } else if (step.duration <= 0) {
        if (this.twistSchedule.length > 1) {
          this.twistSchedule.shift();
          this.render(delta);
          return;
        } else {
          // last step, check if we have a state to activate
          if (this.stateScheduler.length > 0) {
            const nextState = this.stateScheduler[0];
            if (!nextState.condition || nextState.condition()) {
              this.agent.enableState(nextState.state);
              this.stateScheduler.shift();
              return;
            }
          }
        }
      } else {
        this.twist = st(step.twist);
        step.duration -= delta;
      }
      const localDeltaPos = new THREE.Vector3(
        this.twist.linear.x * delta,
        this.twist.linear.y * delta,
        this.twist.linear.z * delta
      );
      const worldDeltaPos = localDeltaPos.applyEuler(this.obj.rotation);
      const localDeltaRot = new THREE.Euler(
        this.twist.angular.x * delta,
        this.twist.angular.y * delta,
        this.twist.angular.z * delta
      );
      this.craft.rotation.x += localDeltaRot.x;
      this.craft.rotation.y += localDeltaRot.y;
      this.craft.rotation.z += localDeltaRot.z;
      this.craft.rotation.x = normalizeAngleToPi(this.craft.rotation.x);
      this.craft.rotation.y = normalizeAngleToPi(this.craft.rotation.y);
      this.craft.rotation.z = normalizeAngleToPi(this.craft.rotation.z);
      // console.log({ localDeltaPos, worldDeltaPos, twist: this.twist });
      this.obj.position.add(worldDeltaPos);
    } else {
      if (this.stateScheduler.length > 0) {
        const nextState = this.stateScheduler[0];
        if (!nextState.condition || nextState.condition()) {
          this.agent.enableState(nextState.state);
          this.stateScheduler.shift();
          return;
        }
      }
      const result = this.controller.computeTwist(delta);
      if (result instanceof DragonflyController) {
        this.startController(result as DragonflyController);
        this.render(delta);
        return; // Recompute with the new controller
      }
      if (typeof result === 'string') {
        this.agent.enableState(result);
        return;
      }
      this.twist = result as Twist;
      if (!this.twist || !this.twist.linear || !this.twist.angular) {
        this.twist = ZeroTwist;

        console.warn(`DragonflyActor: computeTwist invalid from  ${this.controller.constructor.name}, using ZeroTwist`);
      }
      // console.log("Twist aZ: ", this.twist.angular.z);
      const localDeltaPos = new THREE.Vector3(
        this.twist.linear.x * delta,
        this.twist.linear.y * delta,
        this.twist.linear.z * delta
      );
      const worldDeltaPos = localDeltaPos.applyEuler(this.obj.rotation);
      // debugger;
      const localDeltaRot = new THREE.Euler(
        this.twist.angular.x * delta,
        this.twist.angular.y * delta,
        this.twist.angular.z * delta
      );
      this.obj.rotation.x += localDeltaRot.x;
      this.obj.rotation.y += localDeltaRot.y;
      this.obj.rotation.z += localDeltaRot.z;
      this.obj.rotation.x = normalizeAngleToPi(this.obj.rotation.x);
      this.obj.rotation.y = normalizeAngleToPi(this.obj.rotation.y);
      this.obj.rotation.z = normalizeAngleToPi(this.obj.rotation.z);
      const bank = constrain(-this.twist.angular.z * 3, -Math.PI / 3, Math.PI / 3)
      this.craft.rotation.x = normalizeAngleToPi(bank);;
      // console.log({ localDeltaPos, worldDeltaPos, twist: this.twist });
      this.obj.position.add(worldDeltaPos);
    }
    this.cameraController.render(delta);
  }

  // public async loadFromMapObject(mapObject: PosableMapObject) {
  //   await super.loadFromMapObject(mapObject);
  //   const dfMapObject = mapObject as DragonflyMapObject;
  //   this.target = sv(dfMapObject.target);
  //   this.orbitRadius = dfMapObject.orbitRadius || 20.0;
  //   this.orbitAltitude = dfMapObject.orbitAltitude || 10.0;
  //   this.flightPlan = dfMapObject.flightPlan || [];
  // }

  // public saveToMapObject(mapObject: PosableMapObject) {
  //   super.saveToMapObject(mapObject);
  //   const dfMapObject = mapObject as DragonflyMapObject;
  //   dfMapObject.target = sv(this.target);
  //   dfMapObject.orbitRadius = this.orbitRadius;
  //   dfMapObject.orbitAltitude = this.orbitAltitude;
  //   dfMapObject.flightPlan = this.flightPlan;
  // }


  public onSelected(on: boolean): void {
    if (this.controller) {
      this.controller.visualization(on);
    }
  }

}